import { supabase } from '../lib/supabase';
import { getToken } from './megsappAuthService';

const MEGSAPP_BASE_URL = 'https://api.megsapp.com/api';

type SimBalance = {
  balance: number;      // Airtime
  data: number;        // Non-expiry data
  expiry: number;      // Expiry data
  reward: number;      // Rewards data
  subscription: number; // Subscription data
  reverse: number;     // Reverse-billing data
  updated: string;     // Last Megsapp update
  idle_days: string;   // Idle days
};

export async function getSimBalance(msisdn: string): Promise<SimBalance> {
  const token = await getToken();
  
  const endpoint = `${MEGSAPP_BASE_URL}/v1/distributor/customer/balance`;
  console.log(`Fetching balance from: ${endpoint}`);

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ msisdn }),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch balance');
  }

  const { status_code, data } = await response.json();
  if (status_code !== 1001) {
    throw new Error('Invalid status code');
  }

  // Store in sim_cards table
  const { error: simUpdateError } = await supabase
    .from('sim_cards')
    .update({
      balances: data,
      balance_last_checked: new Date().toISOString(),
    })
    .eq('msisdn', msisdn);

  if (simUpdateError) {
    console.error('Failed to update SIM balance:', simUpdateError);
    // Optionally decide if you want to proceed with snapshot if update fails
  }

  // Fetch the sim_card UUID using the msisdn
  const { data: simCard, error: simFetchError } = await supabase
    .from('sim_cards')
    .select('id')
    .eq('msisdn', msisdn)
    .maybeSingle(); // Use maybeSingle to handle case where SIM might not be in DB

  if (simFetchError) {
    console.error(`Error fetching sim_card id for msisdn ${msisdn}:`, simFetchError);
  } else if (simCard) {
    // Store in balance_snapshots table only if simCard was found
    const { error: snapshotError } = await supabase
      .from('balance_snapshots')
      .insert({
        sim_id: simCard.id, // Use the fetched UUID
        balances: { // Structure according to PRD/Megsapp response structure within `data`
          airtime: data.balance, 
          data: data.data,
          expiry: data.expiry,
          reward: data.reward,
          subscription: data.subscription,
          reverse: data.reverse,
        },
        updated: data.updated, // Add from Megsapp response
        idle_days: data.idle_days, // Add from Megsapp response
        timestamp: new Date().toISOString(), // Timestamp of the snapshot itself
      });

    if (snapshotError) {
      console.error('Failed to create balance snapshot:', snapshotError);
    }
  } else {
      console.warn(`Skipping balance snapshot for ${msisdn} because no matching sim_card was found in the database.`);
  }

  // Return the balance data fetched from Megsapp
  return data;
}

/**
 * Trigger an on-demand balance check via the Edge Function
 * This manually invokes the check-balances function for a specific SIM
 */
export async function triggerManualBalanceCheck(msisdn: string): Promise<any> {
  try {
    const { data, error } = await supabase.functions.invoke('check-balances', {
      body: { 
        msisdn,
        mode: 'manual' 
      }
    });
    
    if (error) {
      console.error(`Error invoking check-balances for ${msisdn}:`, error);
      throw error;
    }
    
    console.log(`Manual balance check completed for ${msisdn}:`, data);
    return data;
    
  } catch (error: any) {
    console.error(`Failed to trigger manual balance check for ${msisdn}:`, error);
    throw new Error(`Balance check failed: ${error.message}`);
  }
}

/**
 * Trigger a balance check for multiple SIMs based on criticality
 * This calls the Edge Function with frequency parameter
 */
export async function triggerBatchBalanceCheck(frequency: '15min' | 'hourly'): Promise<any> {
  try {
    const { data, error } = await supabase.functions.invoke('check-balances', {
      body: { frequency }
    });
    
    if (error) {
      console.error(`Error invoking batch check-balances (${frequency}):`, error);
      throw error;
    }
    
    console.log(`Batch balance check (${frequency}) completed:`, data);
    return data;
    
  } catch (error: any) {
    console.error(`Failed to trigger batch balance check (${frequency}):`, error);
    throw new Error(`Batch balance check failed: ${error.message}`);
  }
}

// Type definition for the product object from Megsapp API
type MegsappProduct = {
  id: string; // e.g., "prod_123"
  name: string;
  cost: number;
  category: string;
  // Add other fields if present in the actual API response and needed
};

// Type definition matching your Supabase products table
// (assuming id is now TEXT, is_active is boolean)
type Product = {
  id: string;
  name: string;
  cost: number;
  category: string;
  is_active: boolean;
};

export async function syncMegsappProducts(): Promise<Product[]> {
  console.log('Attempting to sync Megsapp products...');
  try {
    const token = await getToken();
    const endpoint = `${MEGSAPP_BASE_URL}/v1/distributor/products`; // PRD endpoint

    const response = await fetch(endpoint, {
      method: 'POST', // As specified in PRD
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}), // Empty body as specified
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error(`Megsapp products API Error: ${response.status}`, errorBody);
      throw new Error(`Failed to fetch products from Megsapp: ${response.status}`);
    }

    const responseData = await response.json();

    // Check for expected success structure (adjust based on actual API response)
    // PRD example suggests a direct array response, Megsapp API doc might differ
    let productsFromApi: MegsappProduct[] = [];
    if (Array.isArray(responseData)) {
        productsFromApi = responseData;
    } else if (responseData.status_code === 1001 && Array.isArray(responseData.data)) {
        // Handle cases where products are nested under 'data'
        productsFromApi = responseData.data;
    } else {
        console.error('Unexpected response structure from Megsapp products API:', responseData);
        throw new Error('Unexpected response structure fetching products.');
    }

    if (!productsFromApi || productsFromApi.length === 0) {
      console.warn('No products returned from Megsapp API.');
      return [];
    }

    console.log(`Fetched ${productsFromApi.length} products from Megsapp.`);

    // Prepare data for Supabase upsert (map API fields to table columns)
    const productsToUpsert = productsFromApi.map(apiProduct => ({
      id: apiProduct.id, // Assuming products.id is now TEXT
      name: apiProduct.name,
      cost: apiProduct.cost,
      category: apiProduct.category,
      is_active: true // Default to active, or map from API if available
      // created_at/updated_at are handled by Supabase
    }));

    // Upsert into Supabase products table
    const { data: upsertedProducts, error: upsertError } = await supabase
      .from('products')
      .upsert(productsToUpsert)
      .select(); // Select the results to return/log

    if (upsertError) {
      console.error('Failed to upsert products into Supabase:', upsertError);
      throw new Error(`Database Error: Failed to save products: ${upsertError.message}`);
    }

    console.log(`Successfully upserted ${upsertedProducts?.length ?? 0} products into the database.`);
    return (upsertedProducts as Product[]) || [];

  } catch (error) {
    console.error('Error in syncMegsappProducts:', error);
    // Re-throw or return empty array/handle error as appropriate for the caller
    throw error; 
  }
} 